/**
 * Presence scope derivation — pure, dependency-free so it unit-tests without a
 * database (RULE-TS-01).
 *
 * A pathname resolves to an ordered **scope chain**, broadest first:
 *
 *   /internal/C123/networking/vlans            → [customer:C123]
 *   /internal/C123/support/cases/CS001         → [customer:C123, case:CS001]
 *
 * The store writes a heartbeat into EVERY scope in the chain but reads only the
 * last one. That single rule produces the asymmetry the feature is for: someone
 * at customer level reads `customer:C123` and sees the case viewer (who wrote
 * there), while the case viewer reads `case:CS001` and sees only their own
 * level, because nobody at customer level ever wrote there.
 *
 * ── Why a param map and not URL parsing ──────────────────────────────────
 *
 * "Second dynamic segment = a record" is wrong often enough to matter.
 * `/logistics/shipping/[kind]/[orderNo]` looks two deep but `[kind]` is a
 * discriminator (`support` | `project`), not an id — keying a room on it would
 * put every support shipment in one room. So scopes are declared, and
 * **omission is the opt-out**: a param absent from the map never becomes a
 * room. That is also how `serverIp` is handled — a hub server is not a
 * collaborative scope, so a cable modem's chain is `customer → cable-modem`
 * rather than `customer → server → cable-modem`.
 *
 * ── Why names are app-agnostic ───────────────────────────────────────────
 *
 * `case`, not `internal-case`. Scope name + id IS the room, so when
 * `dispatching: { workOrderId: 'work-order' }` is added later, a dispatcher and
 * an internal agent on the same work order land in one room with no redesign.
 * Only `internal` is mapped today; every other app derives an empty chain and
 * gets no presence at all.
 */

/** `<app prefix>` → (`param name` → scope name). Absent param = not a scope. */
const SCOPE_PARAMS: Record<string, Record<string, string>> = {
  internal: {
    // The customer/property level. `[id]` in the internal config keys is the
    // BC customer number (the filesystem segment is `[customerId]`), which is
    // the same id space the technician app uses — so those two share a room
    // for free whenever technician is added to this map.
    id: 'customer',
    // Records beneath a customer.
    caseId: 'case',
    incidentId: 'incident',
    workOrderId: 'work-order',
    opportunityId: 'opportunity',
    jobNo: 'project',
    // Records with no customer in the path (org-level triage / detail pages).
    techId: 'technician',
    callSid: 'voice-call',
    // Sub-records.
    //
    // Deliberately absent: `taskId`. The case-task detail route was the only
    // section-config key that carried it, and case tasks are gone. The
    // work-order task page has an app route but no section-config entry, and
    // deriveScopeChain is only ever called with a config key that matched
    // (resolve-section-match.ts) — so a `taskId` entry here would map nothing.
    // Re-add it alongside the config key, not before.
    emailId: 'case-email',
    macAddress: 'cable-modem',
    serial: 'display'
    // Deliberately absent: `serverIp` (a hub server, not a collaborative
    // scope) and `orderNumber`/`shipmentNumber` (see ROUTE_SCOPE_OVERRIDES —
    // the same param name means two different records depending on the route).
  }
}

/**
 * Route keys whose param name is ambiguous on its own.
 *
 * `/internal/[id]/account/orders/sales/[orderNumber]` and `…/service/…` both
 * call it `orderNumber`, but a sales order SO123 and a service order SO123 are
 * different records — mapping the bare param name would collide them into one
 * room. Same for shipments. Keyed on the full route key, so the ambiguity is
 * resolved where it actually exists rather than by renaming params.
 */
const ROUTE_SCOPE_OVERRIDES: Record<string, Record<string, string>> = {
  '/internal/[id]/account/orders/sales/[orderNumber]': {
    orderNumber: 'sales-order'
  },
  '/internal/[id]/account/orders/service/[orderNumber]': {
    orderNumber: 'service-order'
  },
  '/internal/[id]/account/shipments/sales/[shipmentNumber]': {
    shipmentNumber: 'sales-shipment'
  },
  '/internal/[id]/account/shipments/service/[shipmentNumber]': {
    shipmentNumber: 'service-shipment'
  }
}

/** Ids in this app are UUIDs, ServiceNow numbers, and customer numbers. */
const SAFE_VALUE = /^[A-Za-z0-9_.-]+$/

/** One level of the chain: a scope name and the record it points at. */
export type PresenceScope = {
  /** App-agnostic, e.g. `customer`, `case`, `work-order`. */
  name: string
  /** The room key this scope contributes, e.g. `case:CS0001234`. */
  key: string
}

/** How deep a chain may go before we stop writing — a guard, not a real limit. */
const MAX_SCOPE_DEPTH = 4

/**
 * Derive the scope chain for a matched route, broadest first.
 *
 * `routeKey` is the config key that matched (e.g.
 * `/internal/[id]/support/cases/[caseId]`), NOT the pathname — its dynamic
 * segments give both the order and the param names.
 *
 * Returns an empty array when the route has no scope-bearing param, which is
 * the default opt-out: org dashboards, list pages, reports, utilities and the
 * whole `me` app carry no dynamic params, so they get no presence without
 * anyone having to say so.
 */
// Flat guard clauses over untrusted params (cyclomatic 10 / cognitive low).
// Each branch rejects one specific way a route can fail to produce a chain,
// and collapsing them would hide which check a future change loosened.
// fallow-ignore-next-line complexity
export function deriveScopeChain(
  appPrefix: string,
  routeKey: string,
  params: Record<string, string>
): PresenceScope[] {
  const byParam = SCOPE_PARAMS[appPrefix]
  if (!byParam) return []

  const overrides = ROUTE_SCOPE_OVERRIDES[routeKey]
  const chain: PresenceScope[] = []

  for (const segment of routeKey.split('/')) {
    if (!segment.startsWith('[') || !segment.endsWith(']')) continue
    const paramName = segment.slice(1, -1)

    const scopeName = overrides?.[paramName] ?? byParam[paramName]
    if (!scopeName) continue // not a scope — discriminator, hub server, etc.

    const value = params[paramName]
    // A scope we cannot name a record for breaks the chain rather than
    // silently collapsing two levels into one: a missing customer id must not
    // put a case viewer in the same room as everyone on that route shape.
    if (!value || !SAFE_VALUE.test(value)) return []

    chain.push({ name: scopeName, key: `${scopeName}:${value}` })
    if (chain.length >= MAX_SCOPE_DEPTH) break
  }

  return chain
}
